import type { EditChoice } from "@/common/lib/api/documentEdits";
import { determineChatHeaderAndPatientForMultiplePatientCase } from "@/common/utils/helpers";
import type { MultiPatientData } from "@/common/utils/types";

/** R13: a pet name of "" and null are the same key. Trimmed like the server's `normalizePetName`. */
export const normalizePetName = (name: string | null | undefined): string => (name ?? "").trim();

/** The choices for one pet's documents, or `null` when that pet has none (nothing to send). */
export const choicesForPatient = (choices: EditChoice[] | null, patientName: string): EditChoice[] | null => {
    const forPatient = (choices ?? []).filter(
        (choice) => normalizePetName(choice.patientName) === normalizePetName(patientName),
    );
    return forPatient.length > 0 ? forPatient : null;
};

/** The pet an open document belongs to, from its header, for a multi-pet visit; `undefined` otherwise (R13). */
export const petNamesForHeader = (multiplePatients: MultiPatientData[], header: string): string[] | undefined => {
    const { multiplePatientCase, patientNameToParse } = determineChatHeaderAndPatientForMultiplePatientCase(
        "",
        multiplePatients,
        header,
    );
    return multiplePatientCase && patientNameToParse ? [patientNameToParse] : undefined;
};

/**
 * The pets Transcribe generates one document each for, from the visit's patient name: a mirror of Transcribe's
 * `getMultiplePatientsArray` + `isMultiplePatients` (framework/document/docGen.ts), which split on the word "and"
 * only. Not the app's `checkForMultiplePatientNames`: Stop & Generate registers one pending entry per name, and an
 * entry Transcribe never generates for is never read. Empty for a single-pet visit.
 */
export const petNamesTranscribeGenerates = (patientName: string | null | undefined): string[] => {
    if (typeof patientName !== "string" || !patientName.trim()) return [];
    const parts = patientName.split(/(\band\b)/i);
    if (parts.length <= 2) return [];
    const names = parts.filter((_, index) => index % 2 === 0).map((name) => name.trim());
    return names.length >= 2 ? names : [];
};
